import { preferenceStore } from '@hrms/common/component/user-preference/store';
import axios, { AxiosInstance } from 'axios';
import { X_API_KEY } from '../../constant/global';
import { HTTPResponseCodes } from '../../constant/network';
import AuthService from '../auth';
import {
  AxiosClientConstructorInterface,
  BaseHTTPClientConstructorInterface,
  ErrorInterceptorMiddlewareInterface,
  RequestInterceptorMiddlewareInterface,
  ResponseInterceptorMiddlewareInterface,
} from './types';

abstract class BaseHTTPClient {
  protected readonly instance: AxiosInstance;

  constructor(params: BaseHTTPClientConstructorInterface) {
    this.instance = axios.create(params);
  }

  protected abstract requestInterceptorMiddleware: RequestInterceptorMiddlewareInterface;
  protected abstract responseInterceptorMiddleware: ResponseInterceptorMiddlewareInterface;
  protected abstract errorInterceptorMiddleware: ErrorInterceptorMiddlewareInterface;
}

class AxiosClient extends BaseHTTPClient {
  constructor(params: AxiosClientConstructorInterface) {
    const { HTTPClientParams, isProtected } = params;
    super(HTTPClientParams);

    if (isProtected) {
      this.instance.interceptors.request.use(this.requestInterceptorMiddleware);
    }
    this.instance.interceptors.response.use(
      this.responseInterceptorMiddleware,
      this.errorInterceptorMiddleware,
    );
  }

  protected requestInterceptorMiddleware: RequestInterceptorMiddlewareInterface =
    (config) => {
      const { preference } = preferenceStore.getState();
      const { selectedSite, selectedTenant } = preference ?? {};
      config.headers['Authorization'] = AuthService.getAuthToken();
      config.headers['Content-Type'] =
        config.headers['Content-Type'] || 'application/json';
      config.headers['accept'] = config.headers['accept'] || 'application/json';
      config.headers['x-api-key'] = X_API_KEY;
      config.headers['x-tenant-id'] = selectedTenant?.id;
      config.headers['x-site-id'] =
        config.headers['x-site-id'] ?? selectedSite?.id
          ? selectedSite?.id
          : null;

      return config;
    };

  protected responseInterceptorMiddleware: ResponseInterceptorMiddlewareInterface =
    (response) => {
      if (Object.values(HTTPResponseCodes.SUCCESS).includes(response.status)) {
        return { ...response, data: response.data ?? {} };
      }
      return response;
    };

  protected errorInterceptorMiddleware: ErrorInterceptorMiddlewareInterface = (
    error,
  ) => {
    // TODO: Add toast here
    if (
      error?.response?.status === HTTPResponseCodes.CLIENT_ERROR.UNAUTHORIZED
    ) {
      AuthService.logout();
    }
    return Promise.reject(error);
  };
}

export default AxiosClient;
